import { useState, useEffect, createContext, useContext } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';

interface SubscriptionStatus {
  status: string;
  trialStart: string | null;
  trialEnd: string | null;
  daysRemaining: number;
  isExpired: boolean;
  hasAccess: boolean;
}

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatus | null;
  hasAccess: boolean;
  isLoading: boolean;
  checkAccess: () => Promise<boolean>;
  redirectToSubscription: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const fetchSubscriptionStatus = async (): Promise<SubscriptionStatus | null> => {
    if (!token) return null;

    try {
      // Use only the unified subscription-status endpoint
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Convert snake_case from backend to camelCase for frontend
        const backendData = data.data;
        return {
          status: backendData.status,
          trialStart: backendData.trial_start,
          trialEnd: backendData.trial_end,
          daysRemaining: backendData.days_remaining || 0,
          isExpired: backendData.is_expired || false,
          hasAccess: backendData.has_access ?? true
        };
      } else if (response.status === 404) {
        // User has no subscription record - grant access, backend will create starter on next call
        return {
          status: 'starter',
          trialStart: null,
          trialEnd: null,
          daysRemaining: -1,
          isExpired: false,
          hasAccess: true
        };
      } else {
        console.error('Error obteniendo estado:', response.status, response.statusText);
        // On error, grant access - backend middleware enforces real limits
        return {
          status: 'starter',
          trialStart: null,
          trialEnd: null,
          daysRemaining: -1,
          isExpired: false,
          hasAccess: true
        };
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      // On network error, grant access - backend middleware enforces real limits
      return {
        status: 'error',
        trialStart: null,
        trialEnd: null,
        daysRemaining: -1,
        isExpired: false,
        hasAccess: true
      };
    }
  };

  const checkAccess = async (): Promise<boolean> => {
    // Admins always have access
    if (user?.permisos === 'admin' || user?.permisos === 'superadmin') {
      return true;
    }

    const status = await fetchSubscriptionStatus();
    if (!status) return true;

    setSubscriptionStatus(status);
    return status.hasAccess;
  };

  const redirectToSubscription = () => {
    navigate('/suscripcion');
  };

  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      setIsLoading(true);
      await checkAccess();
      setIsLoading(false);
    };

    if (token) {
      loadSubscriptionStatus();
    } else {
      setIsLoading(false);
    }
  }, [token, user]);

  // Only grant access if subscription status explicitly allows it
  const hasAccess = subscriptionStatus?.hasAccess ?? true;

  return (
    <SubscriptionContext.Provider value={{
      subscriptionStatus,
      hasAccess,
      isLoading,
      checkAccess,
      redirectToSubscription
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionAccess = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionAccess must be used within a SubscriptionProvider');
  }
  return context;
};

// Higher-order component for protecting routes/components
export const withSubscriptionCheck = <P extends object>(
  Component: React.ComponentType<P>,
  fallbackComponent?: React.ComponentType
) => {
  return (props: P) => {
    const { hasAccess, isLoading, redirectToSubscription, subscriptionStatus } = useSubscriptionAccess();
    const { user } = useAuth();

    // Admins always have access
    if (user?.permisos === 'admin' || user?.permisos === 'superadmin') {
      return <Component {...props} />;
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!hasAccess) {
      if (fallbackComponent) {
        const FallbackComponent = fallbackComponent;
        return <FallbackComponent />;
      }

      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                ¡Suscripción Requerida!
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {subscriptionStatus?.status === 'cancel_at_period_end' 
                  ? 'Tu suscripción está programada para finalizar. Renueva para continuar usando GuideMe.' 
                  : subscriptionStatus?.status === 'cancelled' 
                  ? 'Tu suscripción ha finalizado. Suscríbete para continuar usando GuideMe.' 
                  : 'Tu acceso ha expirado. Suscríbete a Pro para acceder a todas las funciones de GuideMe.'}
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={redirectToSubscription}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-semibold"
              >
                Ver Planes de Suscripción
              </button>
              
              <button
                onClick={() => window.history.back()}
                className="w-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-3 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};
